import { useMemo } from 'react';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';

/**
 * Hook personalizado para manejo granular de permisos
 * Proporciona verificaciones específicas por recurso y cache para performance
 */
export const usePermisos = () => {
  const { 
    verificarPermiso, 
    verificarLimite, 
    isSuperAdmin, 
    usuarioEmpresa, 
    planActual,
    limitesUsage
  } = useSimpleEmpresa();

  // Cache de permisos computados para mejor performance
  const permisosComputados = useMemo(() => {
    if (!usuarioEmpresa) return {};

    // Simplificado para compatibilidad con contexto mock
    const computarPermiso = (recurso, accion) => {
      try {
        return verificarPermiso ? verificarPermiso(recurso, accion) : true;
      } catch (error) {
        return isSuperAdmin; // Fallback para SuperAdmin
      }
    };

    return {
      // Permisos de clientes
      clientes: {
        crear: computarPermiso('clientes', 'crear'),
        editar: computarPermiso('clientes', 'editar'),
        eliminar: computarPermiso('clientes', 'eliminar'),
        ver: computarPermiso('clientes', 'ver'),
        exportar: computarPermiso('clientes', 'exportar'),
        importar: computarPermiso('clientes', 'importar')
      },

      // Permisos de vehículos
      vehiculos: {
        crear: computarPermiso('vehiculos', 'crear'),
        editar: computarPermiso('vehiculos', 'editar'),
        eliminar: computarPermiso('vehiculos', 'eliminar'),
        ver: computarPermiso('vehiculos', 'ver'),
        transferir: computarPermiso('vehiculos', 'transferir'),
        historial_completo: computarPermiso('vehiculos', 'historial_completo')
      },

      // Permisos de mantenimientos
      mantenimientos: {
        crear: computarPermiso('mantenimientos', 'crear'),
        editar: computarPermiso('mantenimientos', 'editar'),
        eliminar: computarPermiso('mantenimientos', 'eliminar'),
        ver: computarPermiso('mantenimientos', 'ver'),
        cerrar: computarPermiso('mantenimientos', 'cerrar'),
        ver_asignados: computarPermiso('mantenimientos', 'ver_asignados'),
        ver_todos: computarPermiso('mantenimientos', 'ver_todos')
      },

      // Permisos de citas
      citas: {
        crear: computarPermiso('citas', 'crear'),
        editar: computarPermiso('citas', 'editar'),
        eliminar: computarPermiso('citas', 'eliminar'),
        ver: computarPermiso('citas', 'ver'),
        cancelar: computarPermiso('citas', 'cancelar'),
        reprogramar: computarPermiso('citas', 'reprogramar')
      },

      // Permisos de reportes
      reportes: {
        basicos: computarPermiso('reportes', 'basicos'),
        avanzados: computarPermiso('reportes', 'avanzados'),
        exportar: computarPermiso('reportes', 'exportar'),
        financieros: computarPermiso('reportes', 'financieros'),
        rendimiento: computarPermiso('reportes', 'rendimiento')
      },

      // Permisos de empresa
      empresa: {
        editar: computarPermiso('empresa', 'editar'),
        configurar: computarPermiso('empresa', 'configurar'),
        ver_configuracion: computarPermiso('empresa', 'ver_configuracion'),
        gestionar_planes: computarPermiso('empresa', 'gestionar_planes')
      },

      // Permisos de usuarios
      usuarios: {
        invitar: computarPermiso('usuarios', 'invitar'),
        editar_roles: computarPermiso('usuarios', 'editar_roles'),
        eliminar: computarPermiso('usuarios', 'eliminar'),
        ver_todos: computarPermiso('usuarios', 'ver_todos'),
        gestionar_permisos: computarPermiso('usuarios', 'gestionar_permisos')
      },

      // Permisos de configuración
      configuracion: {
        empresa: computarPermiso('configuracion', 'empresa'),
        precios: computarPermiso('configuracion', 'precios'),
        servicios: computarPermiso('configuracion', 'servicios'),
        sistema: computarPermiso('configuracion', 'sistema'),
        planes: computarPermiso('configuracion', 'planes'),
        billing: computarPermiso('configuracion', 'billing')
      }
    };
  }, [usuarioEmpresa, verificarPermiso, isSuperAdmin]);

  // Verificación de límites del plan con cache
  const limitesComputados = useMemo(() => {
    if (!limitesUsage || !planActual) return {};

    const computarLimite = (tipo) => {
      try {
        return verificarLimite ? verificarLimite(tipo) : { enLimite: false, porcentajeUso: 0 };
      } catch (error) {
        return { enLimite: false, porcentajeUso: 0 };
      }
    };

    return {
      sucursales: computarLimite('sucursales'),
      vehiculos: computarLimite('vehiculos'),
      mantenimientos: computarLimite('mantenimientos'),
      almacenamiento: computarLimite('almacenamiento'),
      usuarios: computarLimite('usuarios')
    };
  }, [limitesUsage, planActual, verificarLimite]);

  // Funciones de verificación específicas por contexto
  const verificaciones = {
    // Verificar si puede realizar acciones en clientes
    canEditClientes: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('clientes', 'editar'));
      } catch { return isSuperAdmin; }
    },
    canCreateClientes: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('clientes', 'crear'));
      } catch { return isSuperAdmin; }
    },
    canDeleteClientes: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('clientes', 'eliminar'));
      } catch { return isSuperAdmin; }
    },
    canViewClientes: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('clientes', 'ver'));
      } catch { return isSuperAdmin; }
    },

    // Verificar acciones en vehículos
    canEditVehiculos: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('vehiculos', 'editar'));
      } catch { return isSuperAdmin; }
    },
    canCreateVehiculos: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('vehiculos', 'crear'));
      } catch { return isSuperAdmin; }
    },
    canDeleteVehiculos: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('vehiculos', 'eliminar'));
      } catch { return isSuperAdmin; }
    },
    canTransferVehiculos: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('vehiculos', 'transferir'));
      } catch { return isSuperAdmin; }
    },

    // Verificar acciones en mantenimientos
    canEditMantenimientos: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('mantenimientos', 'editar'));
      } catch { return isSuperAdmin; }
    },
    canCreateMantenimientos: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('mantenimientos', 'crear'));
      } catch { return isSuperAdmin; }
    },
    canCloseMantenimientos: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('mantenimientos', 'cerrar'));
      } catch { return isSuperAdmin; }
    },
    canViewAllMantenimientos: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('mantenimientos', 'ver_todos'));
      } catch { return isSuperAdmin; }
    },
    canViewAssignedMantenimientos: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('mantenimientos', 'ver_asignados'));
      } catch { return isSuperAdmin; }
    },

    // Verificar acciones en reportes
    canViewBasicReports: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('reportes', 'basicos'));
      } catch { return isSuperAdmin; }
    },
    canViewAdvancedReports: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('reportes', 'avanzados'));
      } catch { return isSuperAdmin; }
    },
    canExportReports: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('reportes', 'exportar'));
      } catch { return isSuperAdmin; }
    },
    canViewReportes: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('reportes', 'ver'));
      } catch { return isSuperAdmin; }
    },

    // Verificar gestión de empresa
    canEditEmpresa: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('empresa', 'editar'));
      } catch { return isSuperAdmin; }
    },
    canConfigureEmpresa: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('empresa', 'configurar'));
      } catch { return isSuperAdmin; }
    },
    canManagePlans: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('empresa', 'gestionar_planes'));
      } catch { return isSuperAdmin; }
    },

    // Verificar gestión de usuarios
    canInviteUsers: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('usuarios', 'invitar'));
      } catch { return isSuperAdmin; }
    },
    canEditUserRoles: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('usuarios', 'editar_roles'));
      } catch { return isSuperAdmin; }
    },
    canDeleteUsers: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('usuarios', 'eliminar'));
      } catch { return isSuperAdmin; }
    },
    canManagePermissions: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('usuarios', 'gestionar_permisos'));
      } catch { return isSuperAdmin; }
    },

    // Verificar configuraciones
    canConfigureSystem: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('configuracion', 'sistema'));
      } catch { return isSuperAdmin; }
    },
    canConfigurePrices: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('configuracion', 'precios'));
      } catch { return isSuperAdmin; }
    },
    canConfigureServices: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('configuracion', 'servicios'));
      } catch { return isSuperAdmin; }
    },
    canManageBilling: () => {
      try {
        return isSuperAdmin || (verificarPermiso && verificarPermiso('configuracion', 'billing'));
      } catch { return isSuperAdmin; }
    }
  };

  // Funciones de verificación de límites
  const limitesVerificaciones = {
    // Verificar si puede agregar más elementos
    canAddSucursal: () => {
      const limite = limitesComputados.sucursales;
      return isSuperAdmin || !limite?.enLimite;
    },

    canAddVehiculo: () => {
      const limite = limitesComputados.vehiculos;
      return isSuperAdmin || !limite?.enLimite;
    },

    canAddMantenimiento: () => {
      const limite = limitesComputados.mantenimientos;
      return isSuperAdmin || !limite?.enLimite;
    },

    // Verificar porcentaje de uso
    getSucursalesUsage: () => limitesComputados.sucursales?.porcentajeUso || 0,
    getVehiculosUsage: () => limitesComputados.vehiculos?.porcentajeUso || 0,
    getMantenimientosUsage: () => limitesComputados.mantenimientos?.porcentajeUso || 0,

    // Verificar si está cerca del límite (80% o más)
    isNearSucursalesLimit: () => limitesComputados.sucursales?.porcentajeUso >= 80,
    isNearVehiculosLimit: () => limitesComputados.vehiculos?.porcentajeUso >= 80,
    isNearMantenimientosLimit: () => limitesComputados.mantenimientos?.porcentajeUso >= 80,

    // Obtener información detallada de límites
    getSucursalesLimitInfo: () => limitesComputados.sucursales,
    getVehiculosLimitInfo: () => limitesComputados.vehiculos,
    getMantenimientosLimitInfo: () => limitesComputados.mantenimientos
  };

  // Funciones de verificación por rol
  const rolesVerificaciones = {
    // Verificar rol específico
    isSuperAdmin: () => isSuperAdmin,
    isAdmin: () => usuarioEmpresa?.perfil_id === 'admin' || isSuperAdmin,
    isMecanico: () => usuarioEmpresa?.perfil_id === 'mecanico',
    isCliente: () => usuarioEmpresa?.perfil_id === 'cliente',

    // Verificar capacidades por rol
    canAccessAdminPanel: () => isSuperAdmin || usuarioEmpresa?.perfil_id === 'admin',
    canAccessMecanicoPanel: () => isSuperAdmin || ['admin', 'mecanico'].includes(usuarioEmpresa?.perfil_id),
    canAccessClientePanel: () => true, // Todos pueden ver su panel de cliente

    // Verificar si tiene rol de gestión
    hasManagementRole: () => isSuperAdmin || ['admin'].includes(usuarioEmpresa?.perfil_id),
    hasOperationalRole: () => isSuperAdmin || ['admin', 'mecanico'].includes(usuarioEmpresa?.perfil_id)
  };

  // Funciones utilitarias para componentes
  const componentHelpers = {
    // Para mostrar/ocultar botones
    shouldShowButton: (requiredPermission) => {
      const [recurso, accion] = requiredPermission.split('.');
      return verificarPermiso(recurso, accion);
    },

    // Para habilitar/deshabilitar controles
    shouldEnableControl: (requiredPermission, checkLimits = false) => {
      const [recurso, accion] = requiredPermission.split('.');
      const hasPermission = verificarPermiso(recurso, accion);
      
      if (!hasPermission) return false;
      if (!checkLimits) return true;

      // Verificar límites si es necesario
      if (recurso === 'vehiculos') return limitesVerificaciones.canAddVehiculo();
      if (recurso === 'sucursales') return limitesVerificaciones.canAddSucursal();
      if (recurso === 'mantenimientos') return limitesVerificaciones.canAddMantenimiento();

      return true;
    },

    // Para mostrar alertas de límites
    getLimitWarning: (tipo) => {
      const limite = limitesComputados[tipo];
      if (!limite) return null;

      if (limite.enLimite) {
        return {
          tipo: 'error',
          mensaje: `Has alcanzado el límite de ${tipo} (${limite.actual}/${limite.maximo})`
        };
      }

      if (limite.porcentajeUso >= 80) {
        return {
          tipo: 'warning',
          mensaje: `Te acercas al límite de ${tipo} (${limite.actual}/${limite.maximo} - ${limite.porcentajeUso}%)`
        };
      }

      return null;
    }
  };

  // Retornar todas las funciones y estados
  return {
    // Estados computados
    permisos: permisosComputados,
    limites: limitesComputados,
    
    // Funciones de verificación
    ...verificaciones,
    ...limitesVerificaciones,
    ...rolesVerificaciones,
    ...componentHelpers,

    // Funciones base del contexto
    verificarPermiso,
    verificarLimite,

    // Estados del usuario
    isSuperAdmin,
    usuarioEmpresa,
    planActual,

    // Función para refrescar cache
    refresh: () => {
      // Los useMemo se actualizarán automáticamente cuando cambien las dependencias
      // Esta función existe para compatibilidad futura
      return true;
    }
  };
};

export default usePermisos;